import { CaretRight, DeviceTabletSpeaker } from '@phosphor-icons/react';
import { Icon, Surface, Text } from '@shh/ui';
import { Link } from 'react-router';
import { useLens } from '../app/lens';
import { useFamily } from '../data/family';
import { settingsText } from '../i18n/settings';
import { startText } from '../i18n/start';
import { firebase } from '../lib/firebase';

/**
 * One quiet line on top of the settings: is this very device in family mode? (Mike, 28.10.: "ganz
 * oben drüber so einen dezenten Hinweis") – a signed-in tablet looks just like a paired one.
 * `onOpen` also opens the section when the address already ends in #geraete.
 */
export function DeviceModeNote({ onOpen }: { onOpen: () => void }) {
  const lens = useLens();
  const f = useFamily();
  const t = settingsText.familyMode;
  const uid = firebase().auth.currentUser?.uid;
  const name = f.devices.find((d) => d.id === uid)?.name || startText.wallName;
  return (
    <div className="flex items-start gap-gap-sm text-sm text-text-secondary">
      <Icon icon={DeviceTabletSpeaker} size="md" className="mt-gap-xs text-accent-secondary" />
      <div className="flex flex-col items-start">
        <p className="pt-gap-xs">{lens.isWall ? t.thisPaired(name) : t.thisSignedIn}</p>
        <Link
          to="/einstellungen#geraete"
          onClick={onOpen}
          className="inline-flex min-h-touch items-center font-semibold text-accent-primary underline"
        >
          {settingsText.sections['geraete'].title}
        </Link>
      </div>
    </div>
  );
}

/**
 * Points from "Familie" to the family mode (Mike, 28.10.: "in den einstellungen bei Familie auch
 * unten noch den Hinweis auf Familienmodus") – families look for devices under their family.
 */
export function FamilyModeHint() {
  const t = settingsText.familyMode;
  return (
    <Surface tone="sunken" className="flex items-start gap-gap-sm">
      <Icon icon={DeviceTabletSpeaker} size="lg" className="text-accent-secondary" />
      <div className="flex flex-1 flex-col items-start gap-gap-xs">
        <Text weight="semibold">{t.hintTitle}</Text>
        <Text size="sm" tone="secondary">
          {t.hintText}
        </Text>
        <Link
          to="/einstellungen#geraete"
          className="inline-flex min-h-touch items-center gap-gap-xs font-semibold text-accent-primary underline"
        >
          {t.hintLink}
          <Icon icon={CaretRight} size="sm" />
        </Link>
      </div>
    </Surface>
  );
}

/**
 * Why pair instead of signing in (1.29, Mike 28.10.: "was ist eigentlich der Unterschied … und
 * warum wird das unterschieden?"). Only what the code really does on a paired device – keep it in
 * step with `lens.isWall` (Shell, Rest, TodayPage) and the website.
 */
export function FamilyModeInfo() {
  const t = settingsText.familyMode;
  return (
    <Surface tone="sunken" className="flex flex-col gap-gap-xs">
      <Text weight="semibold">{t.title}</Text>
      <Text size="sm" tone="secondary">
        {t.intro}
      </Text>
      <ul className="flex list-disc flex-col gap-gap-xs pl-inset-md text-sm text-text-secondary">
        {t.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </Surface>
  );
}
